import React, { useState, useEffect, useRef, createContext, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ChevronDown, Sparkles, Play, Brain, Target, AlertTriangle, 
  TrendingUp, Zap, Users, BarChart3, FlaskConical, GitMerge,
  Building2, Smartphone, Plug, Bot, Eye, Mail, Linkedin,
  Crown, Shield, User, Briefcase, Palette, Settings,
  ArrowRight, Check, Star, Sun, Moon
} from 'lucide-react';

// ================================================
// MIDNIGHT FOREST COLOR PALETTE
// ================================================
const COLORS = {
  dark: {
    background: '#080F0D',
    cardBg: '#0D1810',
    cardBorder: '#1A2E1F',
    cardHover: '#102015',
    navbar: '#050C08',
    navbarBorder: '#1A2E1F',
    primary: '#00E5A0',         // Bioluminescent teal-green
    primaryHover: '#00FFB3',
    primaryDark: '#080F0D',     // Dark text for primary buttons
    secondary: '#E8D5A3',       // Champagne / warm parchment
    tertiary: '#FF6B6B',        // Deep coral
    violet: '#9B7FE8',          // Soft violet
    mutedGreen: '#4A8C6A',      // Deeper muted green
    text: '#EDF5F0',            // Cool clean white with green undertone
    textMuted: '#6B8C78',       // Muted forest green-grey
    inputBorder: '#2A4A35',
    buttonGhost: '#2A4A35',
  },
  light: {
    background: '#F5FAF7',
    cardBg: '#EDF5F0',
    cardBorder: '#C8D9CF',
    cardHover: '#E0EDE5',
    navbar: '#F0F8F4',
    navbarBorder: '#C8D9CF',
    primary: '#00C988',
    primaryHover: '#00E5A0',
    primaryDark: '#080F0D',
    secondary: '#8B7355',
    tertiary: '#E55555',
    violet: '#7B5FD8',
    mutedGreen: '#3A7C5A',
    text: '#080F0D',
    textMuted: '#4A6B58',
    inputBorder: '#A8C4B0',
    buttonGhost: '#6B8C78',
  }
};

// Node colors for neural network (weighted distribution)
const NODE_COLORS_WEIGHTED = [
  // 40% primary green
  '#00E5A0', '#00E5A0', '#00E5A0', '#00E5A0', '#00E5A0', '#00E5A0', '#00E5A0', '#00E5A0',
  // 25% champagne
  '#E8D5A3', '#E8D5A3', '#E8D5A3', '#E8D5A3', '#E8D5A3',
  // 15% coral
  '#FF6B6B', '#FF6B6B', '#FF6B6B',
  // 15% violet
  '#9B7FE8', '#9B7FE8', '#9B7FE8',
  // 5% muted green
  '#4A8C6A'
];

// Problem card accent colors
const PROBLEM_COLORS = ['#00E5A0', '#9B7FE8', '#FF6B6B'];

// ================================================
// THEME CONTEXT
// ================================================
const ThemeContext = createContext();
const useTheme = () => useContext(ThemeContext);

// ================================================
// COGNITIVE LANES DATA
// ================================================
const COGNITIVE_LANES = [
  { name: 'Vision & Expansion', color: '#38BDF8', description: 'Strategic foresight and growth thinking' },
  { name: 'Strategy & Architecture', color: '#1E3A5F', description: 'System design and planning intelligence' },
  { name: 'Creative', color: '#A855F7', description: 'Innovation and artistic expression' },
  { name: 'Marketing', color: '#FB923C', description: 'Brand communication and audience connection' },
  { name: 'Operations', color: '#22C55E', description: 'Process optimization and efficiency' },
  { name: 'Execution', color: '#14B8A6', description: 'Delivery and implementation focus' },
  { name: 'Revenue', color: '#EAB308', description: 'Commercial strategy and growth' },
  { name: 'Relationships', color: '#F472B6', description: 'Partnership and stakeholder management' },
  { name: 'Culture', color: '#C4B5FD', description: 'Team dynamics and organizational health' },
  { name: 'Finance & Analysis', color: '#64748B', description: 'Data-driven decision intelligence' }
];

const COGNITIVE_LEVELS = [
  { level: 'L1', name: 'DO', description: 'Execute defined tasks with precision' },
  { level: 'L2', name: 'RUN', description: 'Manage ongoing operations and processes' },
  { level: 'L3', name: 'IMPROVE', description: 'Optimize systems and solve problems' },
  { level: 'L4', name: 'DECIDE & LEAD', description: 'Set direction and make strategic choices' }
];

const PLATFORM_FEATURES = [
  { name: 'Focus', description: 'Your AI-powered Command Center that prioritizes your day based on your role and cognitive lane', icon: Target },
  { name: 'Insights', description: 'Organizational intelligence that surfaces cognitive load, burnout risk, and execution patterns', icon: BarChart3 },
  { name: 'Pulse', description: 'Real-time momentum tracking showing how your organization is executing right now', icon: TrendingUp },
  { name: 'LAB', description: 'Your AI thinking partner that transforms strategic conversations into executable work', icon: FlaskConical },
  { name: 'Collab', description: 'Cross-lane collaboration intelligence ensuring the right cognitive coverage for complex work', icon: GitMerge },
  { name: 'Structure', description: 'Your living organizational chart that reflects how your company actually thinks', icon: Building2 }
];

const ROLES = [
  { name: 'CEO', description: 'Full organizational visibility with strategic intelligence', benefit: 'See everything that matters', icon: Crown },
  { name: 'National Director', description: 'Cross-regional oversight and coordination', benefit: 'Align national execution', icon: Building2 },
  { name: 'Regional Director', description: 'Regional team management and performance', benefit: 'Drive regional excellence', icon: Users },
  { name: 'Unique IP Director', description: 'Creative division leadership and brand stewardship', benefit: 'Protect creative vision', icon: Palette, special: 'gold' },
  { name: 'Marketing Manager', description: 'Campaign execution and content intelligence', benefit: 'Optimize marketing output', icon: Zap },
  { name: 'Team Member', description: 'Personal productivity and task focus', benefit: 'Clear daily priorities', icon: User },
  { name: 'Chief of Staff', description: 'Executive coordination and organizational bridge', benefit: 'Command the details', icon: Shield, special: 'violet' }
];

const COMING_SOON = [
  { name: 'Mobile Experience', description: 'CognitiveOS in your pocket. Full organizational intelligence wherever you are.', icon: Smartphone },
  { name: 'Integration Hub', description: 'Connect your entire tool ecosystem. 100+ integrations bringing all data into one intelligent layer.', icon: Plug },
  { name: 'AI Coaching', description: 'Personalized cognitive development recommendations based on lane performance and growth trajectory.', icon: Bot },
  { name: 'Client Portal', description: 'Share organizational intelligence with clients and partners in a beautiful read-only view.', icon: Eye }
];

// ================================================
// LIVING NEURAL NETWORK BACKGROUND (Fixed, Full Page)
// ================================================
const LivingNeuralBackground = () => {
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const nodesRef = useRef([]);
  
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    
    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);
    
    // Initialize 3x more nodes (180+ nodes for dense, alive feeling)
    const nodeCount = 200;
    nodesRef.current = Array.from({ length: nodeCount }, () => {
      // Variable sizes: 1px pinpoints, 3px medium, 5px anchors
      const sizeRoll = Math.random();
      let radius;
      if (sizeRoll < 0.5) radius = 0.5 + Math.random() * 0.5; // 50% tiny (0.5-1px)
      else if (sizeRoll < 0.85) radius = 1.5 + Math.random() * 1.5; // 35% medium (1.5-3px)
      else radius = 3 + Math.random() * 2; // 15% large anchors (3-5px)
      
      // Variable speeds for parallax depth effect
      const speedMultiplier = 0.2 + Math.random() * 0.8; // 0.2 to 1.0
      
      return {
        x: Math.random() * window.innerWidth,
        y: Math.random() * window.innerHeight,
        // Curved organic movement - add angle and angular velocity
        angle: Math.random() * Math.PI * 2,
        angularVel: (Math.random() - 0.5) * 0.002,
        speed: (0.1 + Math.random() * 0.15) * speedMultiplier,
        radius,
        baseRadius: radius,
        color: NODE_COLORS_WEIGHTED[Math.floor(Math.random() * NODE_COLORS_WEIGHTED.length)],
        pulse: Math.random() * Math.PI * 2,
        pulseSpeed: 0.01 + Math.random() * 0.02,
        depth: speedMultiplier, // Used for parallax layering
      };
    });
    
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      const nodes = nodesRef.current;
      const width = canvas.width;
      const height = canvas.height;
      
      // Draw connection lines between nearby nodes (very subtle, 8% opacity)
      ctx.lineWidth = 0.5;
      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const dx = nodes[i].x - nodes[j].x;
          const dy = nodes[i].y - nodes[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          
          if (dist < 120) {
            const opacity = (1 - dist / 120) * 0.08; // Max 8% opacity
            ctx.strokeStyle = `rgba(0, 229, 160, ${opacity})`;
            ctx.beginPath();
            ctx.moveTo(nodes[i].x, nodes[i].y);
            ctx.lineTo(nodes[j].x, nodes[j].y);
            ctx.stroke();
          }
        }
      }
      
      // Update and draw nodes
      nodes.forEach(node => {
        // Organic curved movement
        node.angle += node.angularVel;
        node.x += Math.cos(node.angle) * node.speed;
        node.y += Math.sin(node.angle) * node.speed;
        node.pulse += node.pulseSpeed;
        
        // Occasionally change direction slightly for organic feel
        if (Math.random() < 0.001) {
          node.angularVel = (Math.random() - 0.5) * 0.003;
        }
        
        // Wrap around edges smoothly
        if (node.x < -20) node.x = width + 20;
        if (node.x > width + 20) node.x = -20;
        if (node.y < -20) node.y = height + 20;
        if (node.y > height + 20) node.y = -20;
        
        // Gentle pulsing
        const pulseScale = 1 + Math.sin(node.pulse) * 0.2;
        const currentRadius = node.baseRadius * pulseScale;
        
        // Draw glow for larger nodes
        if (node.baseRadius > 2) {
          const gradient = ctx.createRadialGradient(
            node.x, node.y, 0,
            node.x, node.y, currentRadius * 4
          );
          gradient.addColorStop(0, node.color + '40');
          gradient.addColorStop(1, node.color + '00');
          ctx.fillStyle = gradient;
          ctx.beginPath();
          ctx.arc(node.x, node.y, currentRadius * 4, 0, Math.PI * 2);
          ctx.fill();
        }
        
        // Draw node with depth-based opacity (farther = more transparent)
        const depthOpacity = 0.4 + node.depth * 0.6;
        ctx.globalAlpha = depthOpacity;
        ctx.fillStyle = node.color;
        ctx.beginPath();
        ctx.arc(node.x, node.y, currentRadius, 0, Math.PI * 2);
        ctx.fill();
        ctx.globalAlpha = 1;
      });
      
      animationRef.current = requestAnimationFrame(animate);
    };
    
    animate();
    
    return () => {
      window.removeEventListener('resize', resizeCanvas);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);
  
  return (
    <canvas 
      ref={canvasRef}
      className="fixed inset-0 pointer-events-none"
      style={{ zIndex: 0, opacity: 0.7 }}
    />
  );
};

// ================================================
// HERO NEURAL NETWORK (Denser, Concentrated)
// ================================================
const HeroNeuralNetwork = () => {
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const nodesRef = useRef([]);
  
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    const resizeCanvas = () => {
      canvas.width = canvas.offsetWidth * window.devicePixelRatio;
      canvas.height = canvas.offsetHeight * window.devicePixelRatio;
      ctx.scale(window.devicePixelRatio, window.devicePixelRatio);
    };
    
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);
    
    // Extra dense nodes for hero
    const nodeCount = 100;
    nodesRef.current = Array.from({ length: nodeCount }, () => {
      const sizeRoll = Math.random();
      let radius;
      if (sizeRoll < 0.4) radius = 1 + Math.random();
      else if (sizeRoll < 0.8) radius = 2 + Math.random() * 2;
      else radius = 4 + Math.random() * 3;
      
      const speedMultiplier = 0.3 + Math.random() * 0.7;
      
      return {
        x: Math.random() * canvas.offsetWidth,
        y: Math.random() * canvas.offsetHeight,
        angle: Math.random() * Math.PI * 2,
        angularVel: (Math.random() - 0.5) * 0.003,
        speed: (0.15 + Math.random() * 0.2) * speedMultiplier,
        radius,
        baseRadius: radius,
        color: NODE_COLORS_WEIGHTED[Math.floor(Math.random() * NODE_COLORS_WEIGHTED.length)],
        pulse: Math.random() * Math.PI * 2,
        pulseSpeed: 0.015 + Math.random() * 0.015,
        depth: speedMultiplier,
      };
    });
    
    const animate = () => {
      ctx.clearRect(0, 0, canvas.offsetWidth, canvas.offsetHeight);
      
      const nodes = nodesRef.current;
      const width = canvas.offsetWidth;
      const height = canvas.offsetHeight;
      
      // Connection lines
      ctx.lineWidth = 0.5;
      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const dx = nodes[i].x - nodes[j].x;
          const dy = nodes[i].y - nodes[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          
          if (dist < 140) {
            const opacity = (1 - dist / 140) * 0.12;
            ctx.strokeStyle = `rgba(0, 229, 160, ${opacity})`;
            ctx.beginPath();
            ctx.moveTo(nodes[i].x, nodes[i].y);
            ctx.lineTo(nodes[j].x, nodes[j].y);
            ctx.stroke();
          }
        }
      }
      
      // Draw nodes
      nodes.forEach(node => {
        node.angle += node.angularVel;
        node.x += Math.cos(node.angle) * node.speed;
        node.y += Math.sin(node.angle) * node.speed;
        node.pulse += node.pulseSpeed;
        
        if (Math.random() < 0.002) {
          node.angularVel = (Math.random() - 0.5) * 0.004;
        }
        
        if (node.x < -10) node.x = width + 10;
        if (node.x > width + 10) node.x = -10;
        if (node.y < -10) node.y = height + 10;
        if (node.y > height + 10) node.y = -10;
        
        const pulseScale = 1 + Math.sin(node.pulse) * 0.25;
        const currentRadius = node.baseRadius * pulseScale;
        
        // Glow for larger nodes
        if (node.baseRadius > 3) {
          const gradient = ctx.createRadialGradient(
            node.x, node.y, 0,
            node.x, node.y, currentRadius * 5
          );
          gradient.addColorStop(0, node.color + '60');
          gradient.addColorStop(1, node.color + '00');
          ctx.fillStyle = gradient;
          ctx.beginPath();
          ctx.arc(node.x, node.y, currentRadius * 5, 0, Math.PI * 2);
          ctx.fill();
        }
        
        const depthOpacity = 0.5 + node.depth * 0.5;
        ctx.globalAlpha = depthOpacity;
        ctx.fillStyle = node.color;
        ctx.beginPath();
        ctx.arc(node.x, node.y, currentRadius, 0, Math.PI * 2);
        ctx.fill();
        ctx.globalAlpha = 1;
      });
      
      animationRef.current = requestAnimationFrame(animate);
    };
    
    animate();
    
    return () => {
      window.removeEventListener('resize', resizeCanvas);
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
  }, []);
  
  return (
    <canvas 
      ref={canvasRef}
      className="absolute inset-0 w-full h-full"
      style={{ opacity: 0.8 }}
    />
  );
};

// ================================================
// SCROLL ANIMATION HOOK
// ================================================
const useScrollAnimation = () => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setIsVisible(true); },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  
  return [ref, isVisible];
};

// ================================================
// SECTION WRAPPER
// ================================================
const Section = ({ children, className = '', id }) => {
  const [ref, isVisible] = useScrollAnimation();
  return (
    <section 
      id={id}
      ref={ref}
      className={`relative py-24 md:py-32 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'} ${className}`}
    >
      {children}
    </section>
  );
};

// ================================================
// NAV ITEMS CONFIGURATION
// ================================================
const NAV_ITEMS = [
  { label: 'Platform', sectionId: 'platform' },
  { label: 'Framework', sectionId: 'framework' },
  { label: 'Coming Soon', sectionId: 'coming-soon' },
  { label: 'Contact', sectionId: 'early-access' }
];

// ================================================
// MAIN LANDING PAGE
// ================================================
const Landing = () => {
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(true);
  const [activeSection, setActiveSection] = useState('');
  const [formData, setFormData] = useState({
    fullName: '', email: '', company: '', teamSize: '', hearAbout: ''
  });
  const [formSubmitted, setFormSubmitted] = useState(false);
  
  const colors = isDarkMode ? COLORS.dark : COLORS.light;
  const theme = { isDarkMode, colors };
  
  // Handle scroll for navbar background
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  // IntersectionObserver for active section tracking
  useEffect(() => {
    const sectionIds = ['platform', 'framework', 'coming-soon', 'early-access'];
    const observerOptions = {
      root: null,
      rootMargin: '-80px 0px -50% 0px', // Offset for sticky header
      threshold: 0
    };
    
    const observerCallback = (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setActiveSection(entry.target.id);
        }
      });
    };
    
    const observer = new IntersectionObserver(observerCallback, observerOptions);
    
    sectionIds.forEach((id) => {
      const element = document.getElementById(id);
      if (element) observer.observe(element);
    });
    
    return () => observer.disconnect();
  }, []);
  
  const handleFormSubmit = (e) => {
    e.preventDefault();
    setFormSubmitted(true);
    setTimeout(() => {
      setFormSubmitted(false);
      setFormData({ fullName: '', email: '', company: '', teamSize: '', hearAbout: '' });
    }, 3000);
  };
  
  // Smooth scroll with 80px offset for sticky navbar
  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      const navbarHeight = 80;
      const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
      const offsetPosition = elementPosition - navbarHeight;
      
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  const getRoleColor = (role) => {
    if (role.special === 'gold') return colors.secondary;
    if (role.special === 'violet') return colors.violet;
    return colors.primary;
  };

  return (
    <ThemeContext.Provider value={theme}>
      <div className="min-h-screen transition-colors duration-500" style={{ backgroundColor: colors.background }}>
        
        {/* Living Neural Background - Fixed, visible throughout */}
        <LivingNeuralBackground />
        
        {/* ================================================
            FIXED HEADER
            ================================================ */}
        <header 
          className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'py-3' : 'py-5'}`}
          style={{ 
            backgroundColor: scrolled ? (isDarkMode ? 'rgba(5, 12, 8, 0.95)' : 'rgba(240, 248, 244, 0.95)') : 'transparent',
            backdropFilter: scrolled ? 'blur(12px)' : 'none',
            borderBottom: scrolled ? `1px solid ${colors.navbarBorder}` : '1px solid transparent',
          }}
        >
          <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
            {/* Logo */}
            <div className="flex items-center gap-3">
              <div 
                className="w-10 h-10 rounded-xl flex items-center justify-center"
                style={{ 
                  background: `linear-gradient(135deg, ${colors.primary} 0%, #00B880 100%)`,
                  boxShadow: `0 0 25px ${colors.primary}50`
                }}
              >
                <Sparkles className="w-5 h-5" style={{ color: colors.primaryDark }} />
              </div>
              <span className="text-xl font-bold tracking-tight">
                <span style={{ color: colors.text }}>Cognitive</span>
                <span style={{ color: colors.primary }}>OS</span>
              </span>
            </div>
            
            {/* Navigation */}
            <nav className="hidden md:flex items-center gap-8">
              {NAV_ITEMS.map((item) => {
                const isActive = activeSection === item.sectionId;
                return (
                  <button 
                    key={item.label}
                    data-testid={`nav-${item.sectionId}`}
                    onClick={() => scrollToSection(item.sectionId)}
                    className="relative text-sm transition-all duration-200 py-1"
                    style={{ 
                      color: isActive ? colors.primary : colors.textMuted,
                      fontWeight: isActive ? '600' : '400'
                    }}
                    onMouseEnter={(e) => {
                      if (!isActive) e.target.style.color = colors.text;
                    }}
                    onMouseLeave={(e) => {
                      if (!isActive) e.target.style.color = colors.textMuted;
                    }}
                  >
                    {item.label}
                    {/* Active indicator underline */}
                    <span 
                      className="absolute bottom-0 left-0 h-0.5 transition-all duration-300"
                      style={{
                        width: isActive ? '100%' : '0%',
                        backgroundColor: colors.primary,
                        boxShadow: isActive ? `0 0 8px ${colors.primary}` : 'none'
                      }}
                    />
                  </button>
                );
              })}
            </nav>
            
            {/* Right side */}
            <div className="flex items-center gap-4">
              <button
                onClick={() => setIsDarkMode(!isDarkMode)}
                className="p-2.5 rounded-xl transition-all hover:scale-105"
                style={{ backgroundColor: colors.cardBg, border: `1px solid ${colors.cardBorder}` }}
                aria-label="Toggle theme"
              >
                {isDarkMode ? <Sun className="w-5 h-5" style={{ color: colors.secondary }} /> : <Moon className="w-5 h-5" style={{ color: colors.primary }} />}
              </button>
              <button 
                onClick={() => scrollToSection('early-access')}
                className="px-5 py-2.5 rounded-xl text-sm font-semibold transition-all hover:scale-105"
                style={{ 
                  backgroundColor: colors.primary,
                  color: colors.primaryDark,
                  boxShadow: `0 0 25px ${colors.primary}40`
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = colors.primaryHover}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = colors.primary}
              >
                Request Early Access
              </button>
            </div>
          </div>
        </header>

        {/* ================================================
            HERO SECTION
            ================================================ */}
        <section className="relative min-h-screen flex items-center overflow-hidden">
          <div className="absolute right-0 top-0 w-full md:w-2/3 h-full">
            <HeroNeuralNetwork />
          </div>
          
          <div className="relative z-10 max-w-7xl mx-auto px-6 py-32">
            <div className="max-w-2xl">
              {/* Eyebrow - champagne */}
              <div 
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-8"
                style={{ 
                  backgroundColor: `${colors.secondary}15`,
                  border: `1px solid ${colors.secondary}40`,
                }}
              >
                <Sparkles className="w-4 h-4" style={{ color: colors.secondary }} />
                <span className="text-sm font-medium" style={{ color: colors.secondary }}>
                  Organizational Intelligence Platform
                </span>
              </div>
              
              {/* Headline */}
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-black leading-[1.1] mb-6 tracking-tight" style={{ color: colors.text }}>
                The Operating System For How Your Organization Actually{' '}
                <span style={{ color: colors.primary, textShadow: `0 0 50px ${colors.primary}70` }}>Thinks</span>
              </h1>
              
              {/* Subheadline */}
              <p className="text-lg md:text-xl leading-relaxed mb-10 max-w-xl" style={{ color: colors.textMuted }}>
                CognitiveOS maps your team's cognitive landscape, aligns work to how people think, and surfaces the intelligence your organization needs to perform at its highest level.
              </p>
              
              {/* CTAs */}
              <div className="flex flex-col sm:flex-row gap-4 mb-8">
                <button 
                  onClick={() => scrollToSection('early-access')}
                  className="group px-8 py-4 rounded-xl text-base font-bold transition-all hover:scale-105 flex items-center justify-center gap-2"
                  style={{ 
                    backgroundColor: colors.primary,
                    color: colors.primaryDark,
                    boxShadow: `0 0 35px ${colors.primary}50`
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = colors.primaryHover}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = colors.primary}
                >
                  Request Early Access
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </button>
                <button 
                  onClick={() => navigate('/login')}
                  className="px-8 py-4 rounded-xl text-base font-bold transition-all hover:opacity-80 flex items-center justify-center gap-2"
                  style={{ 
                    border: `2px solid ${colors.buttonGhost}`,
                    color: colors.text,
                    backgroundColor: 'transparent'
                  }}
                >
                  <Play className="w-5 h-5" />
                  Watch the Vision
                </button>
              </div>
              
              <p className="text-sm" style={{ color: colors.textMuted }}>
                Trusted by forward-thinking teams. Early access now available.
              </p>
            </div>
          </div>
          
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 animate-bounce">
            <span className="text-xs" style={{ color: colors.textMuted }}>Scroll to explore</span>
            <ChevronDown className="w-5 h-5" style={{ color: colors.textMuted }} />
          </div>
        </section>

        {/* ================================================
            THE CHALLENGE SECTION
            ================================================ */}
        <Section>
          <div style={{ backgroundColor: colors.cardBg }} className="absolute inset-0" />
          <div className="relative max-w-7xl mx-auto px-6">
            <p className="text-sm font-bold uppercase tracking-widest text-center mb-4" style={{ color: colors.secondary }}>
              THE CHALLENGE
            </p>
            <h2 className="text-4xl md:text-5xl font-black text-center mb-16 max-w-4xl mx-auto leading-tight" style={{ color: colors.text }}>
              Your team is brilliant. But the way you work together doesn't reflect it.
            </h2>
            
            <div className="grid md:grid-cols-3 gap-8">
              {[
                { icon: Settings, title: 'Tools without intelligence', description: 'You have twelve tools but none of them understand how your people think. Tasks get assigned to the wrong people for the wrong reasons and performance suffers silently.', color: PROBLEM_COLORS[0] },
                { icon: Brain, title: 'Invisible cognitive load', description: 'You can see what\'s on everyone\'s plate but not what it costs them mentally. Burnout builds slowly and shows up suddenly.', color: PROBLEM_COLORS[1] },
                { icon: AlertTriangle, title: 'Thinking and doing are separate', description: 'Strategy happens in one place. Execution happens somewhere else. The gap between them is where momentum dies.', color: PROBLEM_COLORS[2] }
              ].map((problem, index) => (
                <div 
                  key={index}
                  className="p-8 rounded-2xl transition-all hover:-translate-y-1"
                  style={{ 
                    backgroundColor: colors.cardBg,
                    borderWidth: '1px',
                    borderStyle: 'solid',
                    borderColor: colors.cardBorder,
                    borderLeftWidth: '4px',
                    borderLeftColor: problem.color
                  }}
                >
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-6" style={{ backgroundColor: `${problem.color}20` }}>
                    <problem.icon className="w-6 h-6" style={{ color: problem.color }} />
                  </div>
                  <h3 className="text-xl font-bold mb-4" style={{ color: colors.text }}>{problem.title}</h3>
                  <p style={{ color: colors.textMuted }} className="leading-relaxed">{problem.description}</p>
                </div>
              ))}
            </div>
          </div>
        </Section>

        {/* ================================================
            COGNITIVE FRAMEWORK SECTION
            ================================================ */}
        <Section id="framework">
          <div className="max-w-7xl mx-auto px-6">
            <p className="text-sm font-bold uppercase tracking-widest text-center mb-4" style={{ color: colors.secondary }}>
              THE COGNITIVE FRAMEWORK
            </p>
            <h2 className="text-4xl md:text-5xl font-black text-center mb-4 leading-tight" style={{ color: colors.text }}>
              Ten ways of thinking. One operating system.
            </h2>
            <p className="text-lg text-center mb-16 max-w-3xl mx-auto" style={{ color: colors.textMuted }}>
              CognitiveOS is built on a proprietary framework that recognizes the ten distinct cognitive lanes every organization operates across — and the four levels at which every person engages with their work.
            </p>
            
            {/* Cognitive Lanes */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-16">
              {COGNITIVE_LANES.map((lane, index) => (
                <div 
                  key={index}
                  className="group p-5 rounded-xl transition-all hover:-translate-y-1 cursor-pointer"
                  style={{ 
                    backgroundColor: colors.cardBg,
                    borderWidth: '1px',
                    borderStyle: 'solid',
                    borderColor: colors.cardBorder,
                    borderLeftWidth: '3px',
                    borderLeftColor: lane.color
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = colors.cardHover;
                    e.currentTarget.style.boxShadow = `0 8px 30px ${lane.color}25`;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = colors.cardBg;
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <div className="w-3 h-3 rounded-full mb-3" style={{ backgroundColor: lane.color }} />
                  <h4 className="text-sm font-bold mb-1" style={{ color: colors.text }}>{lane.name}</h4>
                  <p className="text-xs" style={{ color: colors.textMuted }}>{lane.description}</p>
                </div>
              ))}
            </div>
            
            {/* Cognitive Levels */}
            <div className="flex flex-col md:flex-row items-stretch justify-center gap-4 md:gap-6 mb-16">
              {COGNITIVE_LEVELS.map((level, index) => (
                <div 
                  key={index}
                  className="flex-1 p-6 rounded-xl text-center transition-all hover:-translate-y-1"
                  style={{ 
                    backgroundColor: colors.cardBg,
                    borderWidth: '1px',
                    borderStyle: 'solid',
                    borderColor: colors.cardBorder,
                    borderTopWidth: '4px',
                    borderTopColor: colors.primary,
                    minWidth: '200px'
                  }}
                >
                  <div className="text-3xl font-black mb-2" style={{ color: colors.primary }}>{level.level}</div>
                  <div className="text-lg font-bold mb-2" style={{ color: colors.text }}>{level.name}</div>
                  <p className="text-sm" style={{ color: colors.textMuted }}>{level.description}</p>
                </div>
              ))}
            </div>
            
            <div className="max-w-3xl mx-auto p-8 rounded-2xl text-center" style={{ backgroundColor: colors.cardBg, border: `1px solid ${colors.primary}30` }}>
              <p className="text-lg leading-relaxed" style={{ color: colors.textMuted }}>
                This isn't a methodology we borrowed. It's an <span style={{ color: colors.text }} className="font-semibold">original system</span> built from years of studying how human cognition actually operates inside organizations. Every feature in CognitiveOS is an expression of this framework.
              </p>
            </div>
          </div>
        </Section>

        {/* ================================================
            PLATFORM SECTION
            ================================================ */}
        <Section id="platform">
          <div style={{ backgroundColor: colors.cardBg }} className="absolute inset-0" />
          <div className="relative max-w-7xl mx-auto px-6">
            <p className="text-sm font-bold uppercase tracking-widest text-center mb-4" style={{ color: colors.secondary }}>
              THE PLATFORM
            </p>
            <h2 className="text-4xl md:text-5xl font-black text-center mb-4 leading-tight" style={{ color: colors.text }}>
              Intelligence at every level of your organization
            </h2>
            <p className="text-lg text-center mb-16 max-w-3xl mx-auto" style={{ color: colors.textMuted }}>
              From the CEO's strategic view to the team member's daily focus — CognitiveOS personalizes the experience for every role, every lane, and every level.
            </p>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {PLATFORM_FEATURES.map((feature, index) => (
                <div 
                  key={index}
                  className="group p-8 rounded-2xl transition-all hover:-translate-y-2 cursor-pointer"
                  style={{ 
                    backgroundColor: colors.background,
                    borderWidth: '1px',
                    borderStyle: 'solid',
                    borderColor: colors.cardBorder,
                    borderTopWidth: '3px',
                    borderTopColor: colors.primary
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.boxShadow = `0 20px 40px ${colors.primary}15`}
                  onMouseLeave={(e) => e.currentTarget.style.boxShadow = 'none'}
                >
                  <div 
                    className="w-14 h-14 rounded-xl flex items-center justify-center mb-6 transition-transform group-hover:scale-110"
                    style={{ background: `linear-gradient(135deg, ${colors.primary}30 0%, ${colors.primary}10 100%)`, border: `1px solid ${colors.primary}40` }}
                  >
                    <feature.icon className="w-7 h-7" style={{ color: colors.primary }} />
                  </div>
                  <h3 className="text-xl font-bold mb-3" style={{ color: colors.text }}>{feature.name}</h3>
                  <p style={{ color: colors.textMuted }} className="leading-relaxed">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </Section>

        {/* ================================================
            ROLES SECTION
            ================================================ */}
        <Section>
          <div className="max-w-7xl mx-auto px-6">
            <p className="text-sm font-bold uppercase tracking-widest text-center mb-4" style={{ color: colors.secondary }}>
              BUILT FOR EVERYONE
            </p>
            <h2 className="text-4xl md:text-5xl font-black text-center mb-4 leading-tight" style={{ color: colors.text }}>
              One platform. Seven perspectives. Infinite clarity.
            </h2>
            <p className="text-lg text-center mb-16 max-w-3xl mx-auto" style={{ color: colors.textMuted }}>
              CognitiveOS doesn't show everyone the same thing. Every role sees exactly what they need — nothing more, nothing less.
            </p>
            
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 mb-12">
              {ROLES.map((role, index) => {
                const roleColor = getRoleColor(role);
                return (
                  <div 
                    key={index}
                    className="group p-5 rounded-xl transition-all hover:-translate-y-1 cursor-pointer text-center"
                    style={{ backgroundColor: colors.cardBg, border: `1px solid ${roleColor}30` }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = `${roleColor}10`;
                      e.currentTarget.style.borderColor = `${roleColor}60`;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = colors.cardBg;
                      e.currentTarget.style.borderColor = `${roleColor}30`;
                    }}
                  >
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-4 transition-all group-hover:scale-110" style={{ backgroundColor: `${roleColor}20` }}>
                      <role.icon className="w-6 h-6" style={{ color: roleColor }} />
                    </div>
                    <h4 className="text-sm font-bold mb-1" style={{ color: colors.text }}>{role.name}</h4>
                    <p className="text-xs mb-2 hidden md:block" style={{ color: colors.textMuted }}>{role.description}</p>
                    <p className="text-xs font-medium" style={{ color: roleColor }}>{role.benefit}</p>
                  </div>
                );
              })}
            </div>
            
            <p className="text-center max-w-2xl mx-auto" style={{ color: colors.textMuted }}>
              From the executive overseeing the entire organization to the team member executing daily tasks — CognitiveOS gives every person the <span style={{ color: colors.text }} className="font-semibold">intelligence layer their role deserves</span>.
            </p>
          </div>
        </Section>

        {/* ================================================
            COMING SOON SECTION
            ================================================ */}
        <Section id="coming-soon">
          <div style={{ backgroundColor: colors.cardBg }} className="absolute inset-0" />
          <div className="relative max-w-7xl mx-auto px-6">
            <p className="text-sm font-bold uppercase tracking-widest text-center mb-4" style={{ color: colors.secondary }}>
              WHAT'S COMING
            </p>
            <h2 className="text-4xl md:text-5xl font-black text-center mb-16 leading-tight" style={{ color: colors.text }}>
              The future of organizational intelligence is being built right now
            </h2>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {COMING_SOON.map((feature, index) => (
                <div 
                  key={index}
                  className="group relative p-8 rounded-2xl overflow-hidden transition-all hover:-translate-y-1"
                  style={{ backgroundColor: colors.background, border: `1px solid ${colors.tertiary}30` }}
                >
                  <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity" style={{ background: `linear-gradient(90deg, transparent, ${colors.tertiary}08, transparent)`, animation: 'shimmer 2s infinite' }} />
                  <div className="relative">
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-6" style={{ backgroundColor: `${colors.tertiary}15` }}>
                      <feature.icon className="w-6 h-6" style={{ color: colors.tertiary }} />
                    </div>
                    <div className="flex items-center gap-2 mb-3 flex-wrap">
                      <h3 className="text-lg font-bold" style={{ color: colors.text }}>{feature.name}</h3>
                      <span className="text-xs font-bold px-3 py-1 rounded-full" style={{ backgroundColor: colors.tertiary, color: '#FFFFFF' }}>
                        Coming Soon
                      </span>
                    </div>
                    <p className="text-sm leading-relaxed" style={{ color: colors.textMuted }}>{feature.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Section>

        {/* ================================================
            EARLY ACCESS SECTION
            ================================================ */}
        <Section id="early-access">
          <div className="max-w-4xl mx-auto px-6">
            <h2 className="text-4xl md:text-5xl font-black text-center mb-4 leading-tight" style={{ color: colors.text }}>
              Be among the first to experience it
            </h2>
            <p className="text-lg text-center mb-12 max-w-2xl mx-auto" style={{ color: colors.textMuted }}>
              CognitiveOS is currently in early access. We are onboarding a select group of forward-thinking teams who want to experience organizational intelligence before anyone else.
            </p>
            
            <form onSubmit={handleFormSubmit} className="max-w-xl mx-auto mb-12">
              <div className="p-8 rounded-2xl" style={{ backgroundColor: colors.cardBg, border: `1px solid ${colors.cardBorder}` }}>
                {formSubmitted ? (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: `${colors.primary}20` }}>
                      <Check className="w-8 h-8" style={{ color: colors.primary }} />
                    </div>
                    <h3 className="text-xl font-bold mb-2" style={{ color: colors.text }}>Request Submitted!</h3>
                    <p style={{ color: colors.textMuted }}>We'll review your application and get back to you within 48 hours.</p>
                  </div>
                ) : (
                  <div className="space-y-5">
                    {[
                      { label: 'Full Name', type: 'text', key: 'fullName', placeholder: 'John Smith' },
                      { label: 'Email Address', type: 'email', key: 'email', placeholder: 'john@company.com' },
                      { label: 'Company Name', type: 'text', key: 'company', placeholder: 'Acme Inc.' },
                    ].map((field) => (
                      <div key={field.key}>
                        <label className="block text-sm font-medium mb-2" style={{ color: colors.textMuted }}>{field.label}</label>
                        <input 
                          type={field.type}
                          value={formData[field.key]}
                          onChange={(e) => setFormData({...formData, [field.key]: e.target.value})}
                          className="w-full px-4 py-3 rounded-xl transition-all focus:outline-none"
                          style={{ backgroundColor: colors.background, border: `1px solid ${colors.inputBorder}`, color: colors.text }}
                          placeholder={field.placeholder}
                          required
                        />
                      </div>
                    ))}
                    <div>
                      <label className="block text-sm font-medium mb-2" style={{ color: colors.textMuted }}>Team Size</label>
                      <select 
                        value={formData.teamSize}
                        onChange={(e) => setFormData({...formData, teamSize: e.target.value})}
                        className="w-full px-4 py-3 rounded-xl transition-all focus:outline-none"
                        style={{ backgroundColor: colors.background, border: `1px solid ${colors.inputBorder}`, color: colors.text }}
                        required
                      >
                        <option value="">Select team size</option>
                        {['1-10', '11-25', '26-50', '51-100', '100+'].map(size => <option key={size} value={size}>{size}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2" style={{ color: colors.textMuted }}>How did you hear about us?</label>
                      <input 
                        type="text"
                        value={formData.hearAbout}
                        onChange={(e) => setFormData({...formData, hearAbout: e.target.value})}
                        className="w-full px-4 py-3 rounded-xl transition-all focus:outline-none"
                        style={{ backgroundColor: colors.background, border: `1px solid ${colors.inputBorder}`, color: colors.text }}
                        placeholder="e.g., LinkedIn, referral, search..."
                      />
                    </div>
                    <button 
                      type="submit"
                      className="w-full py-4 rounded-xl text-base font-bold transition-all hover:scale-[1.02]"
                      style={{ backgroundColor: colors.primary, color: colors.primaryDark, boxShadow: `0 0 30px ${colors.primary}40` }}
                    >
                      Submit Request
                    </button>
                    <p className="text-xs text-center" style={{ color: colors.textMuted }}>
                      We review every application personally. You'll hear from us within 48 hours.
                    </p>
                  </div>
                )}
              </div>
            </form>
            
            <div className="flex flex-col md:flex-row items-center justify-center gap-8">
              {[
                { icon: Star, text: 'Early access pricing locked for life' },
                { icon: Users, text: 'Direct access to the founding team' },
                { icon: Target, text: 'Shape the product roadmap' }
              ].map((benefit, index) => (
                <div key={index} className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: colors.cardBg }}>
                    <benefit.icon className="w-5 h-5" style={{ color: colors.primary }} />
                  </div>
                  <span className="text-sm" style={{ color: colors.textMuted }}>{benefit.text}</span>
                </div>
              ))}
            </div>
          </div>
        </Section>

        {/* ================================================
            FOOTER
            ================================================ */}
        <footer className="py-16 border-t" style={{ backgroundColor: colors.background, borderColor: colors.cardBorder }}>
          <div className="max-w-7xl mx-auto px-6">
            <div className="flex flex-col md:flex-row items-start justify-between gap-12 mb-12">
              <div>
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: `linear-gradient(135deg, ${colors.primary} 0%, #00B880 100%)` }}>
                    <Sparkles className="w-5 h-5" style={{ color: colors.primaryDark }} />
                  </div>
                  <span className="text-xl font-bold tracking-tight">
                    <span style={{ color: colors.text }}>Cognitive</span>
                    <span style={{ color: colors.primary }}>OS</span>
                  </span>
                </div>
                <p className="text-sm" style={{ color: colors.textMuted }}>Creative Intelligence</p>
              </div>
              
              <div className="flex flex-wrap gap-8">
                {['About', 'Platform', 'Framework', 'Early Access', 'Contact', 'Privacy Policy'].map((item) => (
                  <button 
                    key={item}
                    onClick={() => {
                      if (item === 'Platform') scrollToSection('platform');
                      else if (item === 'Framework') scrollToSection('framework');
                      else scrollToSection('early-access');
                    }}
                    className="text-sm transition-colors"
                    style={{ color: colors.textMuted }}
                    onMouseEnter={(e) => e.target.style.color = colors.text}
                    onMouseLeave={(e) => e.target.style.color = colors.textMuted}
                  >
                    {item}
                  </button>
                ))}
              </div>
              
              <div className="flex items-center gap-4">
                <a href="mailto:hello@cognitiveos.io" className="flex items-center gap-2 text-sm transition-colors" style={{ color: colors.textMuted }}>
                  <Mail className="w-4 h-4" /> hello@cognitiveos.io
                </a>
                <a href="#" className="flex items-center gap-2 text-sm transition-colors" style={{ color: colors.textMuted }}>
                  <Linkedin className="w-4 h-4" /> LinkedIn
                </a>
              </div>
            </div>
            
            <div className="pt-8 border-t flex flex-col md:flex-row items-center justify-between gap-4" style={{ borderColor: colors.cardBorder }}>
              <p className="text-xs" style={{ color: colors.textMuted }}>&copy; {new Date().getFullYear()} CognitiveOS. All rights reserved.</p>
              <p className="text-xs" style={{ color: colors.textMuted }}>CognitiveOS and the Cognitive Lanes framework are proprietary intellectual property.</p>
            </div>
          </div>
        </footer>

        <style>{`
          @keyframes shimmer {
            0% { transform: translateX(-100%); }
            100% { transform: translateX(100%); }
          }
          html { scroll-behavior: smooth; }
          input:focus, select:focus {
            border-color: ${colors.primary} !important;
            box-shadow: 0 0 0 2px ${colors.primary}20 !important;
          }
          select option { background-color: ${colors.cardBg}; color: ${colors.text}; }
          ::placeholder { color: ${colors.textMuted}; opacity: 0.6; }
        `}</style>
      </div>
    </ThemeContext.Provider>
  );
};

export default Landing;
